import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, CardHeader, CardTitle, Button } from 'reactstrap';
import { Icon } from 'js/components';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import TeamMember from './TeamDashboard/TeamMember';
import { AddMemberForm, EditMemberForm } from './TeamDashboard/MemberForm';
import { isArchived } from 'js/utils/team-utils';

function TeamMembers({
  team,
  onAddTeamMember = () => {},
  onUpdateTeamMember = () => {},
  onRemoveTeamMember = () => {},
}) {
  const members = team?.members ?? [];
  const [toRemoveMember, setToRemoveMember] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);

  const toggleAddTeamMember = () => {
    setIsAddingTeamMember(!isAddingTeamMember);
    setEditingMemberId(null);
  };

  const handleAddTeamMember = async (member) => {
    if (!team?.id) return null;
    const result = await onAddTeamMember(team.id, member);
    setIsAddingTeamMember(false);
    return result;
  };

  const handleUpdateTeamMember = async (member, values) => {
    const result = await onUpdateTeamMember({ ...member, ...values });
    setEditingMemberId(null);
    return result;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="action-items">
            {isAddingTeamMember ? (
              <Button color="secondary" onClick={toggleAddTeamMember}>
                Cancel
              </Button>
            ) : (
              members?.length > 0 && (
                <Button
                  color="primary"
                  onClick={toggleAddTeamMember}
                  disabled={!team || isArchived(team)}
                >
                  <Icon icon="plus" className="btn-icon-left" />
                  Add member
                </Button>
              )
            )}
          </div>
          <CardTitle tag="h5">Team Members</CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="list-unstyled team-member-list">
            {isAddingTeamMember && (
              <li>
                <AddMemberForm
                  onAddMember={handleAddTeamMember}
                  text={
                    members?.length > 0
                      ? 'Who else is on your team?'
                      : 'Who is on your team?'
                  }
                  onCancel={() => setIsAddingTeamMember(false)}
                />
              </li>
            )}
            {members && members.length ? (
              members.map((m) =>
                m.member_id === editingMemberId ? (
                  <li key={m.member_id} className="edit-form">
                    <EditMemberForm
                      teamMember={m}
                      onCancel={() => setEditingMemberId(null)}
                      onSave={handleUpdateTeamMember}
                      onDelete={() => setToRemoveMember(m)}
                    />
                  </li>
                ) : (
                  <TeamMember
                    key={m.member_id}
                    teamMember={m}
                    className="team-member"
                    onEdit={() => {
                      setIsAddingTeamMember(false);
                      setEditingMemberId(m.member_id);
                    }}
                  />
                )
              )
            ) : (
              <>
                {isAddingTeamMember ? null : (
                  <div className="media text-muted">
                    <Icon
                      icon={['fas', 'users']}
                      size="4x"
                      className="mr-3"
                      fixedWidth
                    />
                    <div className="media-body">
                      <p>You haven't added any team members yet.</p>
                      <p>
                        Adding people to your team lets you track changes to
                        team composition that may impact your effectiveness. It
                        also unlocks some handy shortcuts for managing your
                        assessment for teams and organizations.
                      </p>
                      <p>
                        <Button
                          color="primary"
                          onClick={toggleAddTeamMember}
                          disabled={!team || isArchived(team)}
                        >
                          <Icon icon="plus" className="btn-icon-left" />
                          Add some team members now
                        </Button>
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </ul>
        </CardBody>
      </Card>
      <ConfirmModal
        isOpen={!!toRemoveMember}
        title="Remove team member?"
        onConfirm={async () => {
          if (!team?.id || !toRemoveMember?.member_id) {
            setToRemoveMember(null);
            return null;
          }
          const remove = await onRemoveTeamMember(
            team.id,
            toRemoveMember?.member_id
          );
          setToRemoveMember(null);
          return remove;
        }}
        onCancel={() => setToRemoveMember(null)}
        confirmButtonLabel="Remove User"
      >
        <p>
          Please confirm that you want to remove{' '}
          <strong>{toRemoveMember?.name ?? toRemoveMember?.email}</strong> from
          the team: <strong>{team?.name ?? 'this team'}.</strong>
        </p>
      </ConfirmModal>
    </>
  );
}

TeamMembers.propTypes = {
  team: PropTypes.object.isRequired,
  onAddTeamMember: PropTypes.func,
  onUpdateTeamMember: PropTypes.func,
  onRemoveTeamMember: PropTypes.func,
};

export default TeamMembers;
